import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useRootRunAndRun } from '@/Pages/Run/utils/useRootRunAndRun';
import { useOrganizationId } from '@/hooks/useSwr';
import { RunSchema } from '@/types/schema';
import {
  appAnnotationQueuesPath,
  appGraphIndexPath,
  appOrganizationPath,
  appPlaygroundPath,
  appPublicDatasetsPath,
  appPublicPath,
  appRunPath,
  appSessionPath,
} from '@/utils/constants';

// todo: determine how to consolidate this (path routes as well as search params stuff)
export const useNavigateToPlaygroundFromRun = (
  priorThreadRunIds?: string[]
) => {
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  const {
    annotationQueueId,
    datasetShareToken,
    shareToken,
    runId,
    sessionId,
    conversationId,
    threadId,
  } = useParams();
  const [searchParams] = useSearchParams();
  const traceId = searchParams.get('trace_id') ?? undefined;
  const hostProjectId = searchParams.get('hostProjectId') ?? undefined;

  // Used for public run pages
  const { rootRun } = useRootRunAndRun(
    traceId,
    runId,
    {
      runShareToken: shareToken,
    },
    undefined,
    undefined,
    sessionId
  );

  const searchParamsString = new URLSearchParams({
    ...(rootRun?.trace_id && rootRun?.trace_id === rootRun?.id
      ? { trace_id: rootRun.trace_id }
      : {}),
    ...(rootRun?.start_time ? { start_time: rootRun?.start_time } : {}),
  }).toString();

  const getBasePath = (run: RunSchema) => {
    return `/${appOrganizationPath}/${organizationId}/${appPlaygroundPath}/${appRunPath}/${run.id}`;
  };

  const getPublicPath = (run: RunSchema) => {
    const basePath = `/${appPublicPath}/${shareToken}/${appPlaygroundPath}/${appRunPath}`;
    return run.id === rootRun?.id
      ? `${basePath}?${searchParamsString}`
      : `${basePath}/${run.id}?${searchParamsString}`;
  };

  const getDatasetPath = (run: RunSchema) =>
    `/${appPublicPath}/${datasetShareToken}/${appPublicDatasetsPath}/${
      sessionId ?? run.session_id
    }/${appSessionPath}/${appRunPath}/${
      run.id
    }/${appPlaygroundPath}?${searchParamsString}`;

  const getAnnotationQueuePath = (run: RunSchema) => {
    const base = `/${appOrganizationPath}/${organizationId}/${appAnnotationQueuesPath}/${annotationQueueId}`;
    if (sessionId) {
      return `${base}/${sessionId}/${appPlaygroundPath}/${appRunPath}/${run.id}`;
    }
    return `${base}/${appPlaygroundPath}/${appRunPath}/${
      run.id
    }?${searchParams.toString()}`;
  };

  const getThreadsPath = (run: RunSchema) => {
    if (!priorThreadRunIds || !conversationId) return;
    const query = new URLSearchParams({
      history: priorThreadRunIds.join(','),
      thread_id: conversationId,
      project_id: sessionId ?? '',
    }).toString();
    return `/${appOrganizationPath}/${organizationId}/${appPlaygroundPath}/${appRunPath}/${run.id}?${query}`;
  };

  const getGraphPath = (run: RunSchema) => {
    const query = new URLSearchParams({
      hostProjectId: hostProjectId ?? '',
    }).toString();
    return `/${appOrganizationPath}/${organizationId}/${appGraphIndexPath}/thread/${threadId}/${appPlaygroundPath}/${appRunPath}/${
      run.id
    }${query ? `?${query}` : ''}`;
  };

  const getPathToNavigateTo = (run: RunSchema) => {
    if (shareToken) {
      return getPublicPath(run);
    }
    if (datasetShareToken) {
      return getDatasetPath(run);
    }
    if (annotationQueueId) {
      return getAnnotationQueuePath(run);
    }
    if (priorThreadRunIds && conversationId) {
      return getThreadsPath(run);
    }
    if (threadId) {
      return getGraphPath(run);
    }
    return getBasePath(run);
  };

  return (run: RunSchema) => {
    const path = getPathToNavigateTo(run);
    if (path) {
      navigate(path);
    }
  };
};
